import { useMemo } from 'react'

import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'

interface Params {
  totalPrice?: number
  totalMemberPrice?: number
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
}

/**
 * Shared burn / view-in-points state for an experience search tile (pricing + earn).
 */
export default function useExperienceSearchTileBurn(params: Params) {
  const { totalPrice, totalMemberPrice, isLuxPlusEnabled, isLoyaltyEnabled } = params
  const { searchFilters } = useExperiencesSearchFilters()
  const { hasSelectedBurnToggle, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()

  const viewPriceInPointsFromUrl = !!searchFilters.viewPriceInPoints
  const showLuxPlusPricing = isLuxPlusEnabled && !!totalMemberPrice

  // Burn slider wins if URL still has viewPriceInPoints during a transition.
  const viewPriceInPoints = viewPriceInPointsFromUrl && !hasSelectedBurnToggle
  const isSocietePricingActive = viewPriceInPoints || hasSelectedBurnToggle

  const nonMemberBurn = useSearchBurnPoints(totalPrice ?? 0, 'experience', undefined, {
    disabled: !isLoyaltyEnabled || !totalPrice || !isSocietePricingActive,
    viewPriceInPoints,
  })

  const memberBurn = useSearchBurnPoints(totalMemberPrice ?? 0, 'experience', undefined, {
    disabled: !isLoyaltyEnabled || !showLuxPlusPricing || !isSocietePricingActive,
    viewPriceInPoints,
  })

  const priceInPointsForEarn = showLuxPlusPricing && memberBurn.priceInPoints
    ? memberBurn.priceInPoints
    : nonMemberBurn.priceInPoints

  const pointsToApplyForEarn = useMemo(() => {
    if (!isSocietePricingActive || !priceInPointsForEarn) {
      return undefined
    }
    if (viewPriceInPoints) {
      return priceInPointsForEarn
    }
    if (hasSelectedBurnToggle && contextPointsToBurn !== undefined) {
      return Math.min(contextPointsToBurn, priceInPointsForEarn)
    }
    return undefined
  }, [
    contextPointsToBurn,
    hasSelectedBurnToggle,
    isSocietePricingActive,
    priceInPointsForEarn,
    viewPriceInPoints,
  ])

  return {
    hasSelectedBurnToggle,
    viewPriceInPoints,
    isSocietePricingActive,
    showLuxPlusPricing,
    nonMemberBurn,
    memberBurn,
    pointsToApplyForEarn,
    priceInPointsForEarn,
  }
}

export type ExperienceSearchTileBurn = ReturnType<typeof useExperienceSearchTileBurn>
